import { useMemo, useState } from 'react';
import { api, useQuery } from '../api.js';
import { SearchField, matchesSearch } from '../controls.js';
import { Avatar, AvatarPicker } from '../avatar.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';

interface Cell { date: string; hours: number; state: string }
interface InstructorRow {
  id: number; userId: number; avatarUpdatedAt: number | null;
  name: string; ratings: string; nightQualified: boolean;
  cfiExpiresOn: string | null; color: string; isMe: boolean;
  todayHours: number; weekHours: number;
  availability: { weekday: number; start_min: number; end_min: number }[];
  cells: Cell[];
}
interface Data {
  weekStart: string;
  days: { date: string; dow: string; day: string }[];
  instructors: InstructorRow[];
  maxInstructionHours: number;
  maxDutyHours: number;
  canManage: boolean;
}

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function Instructors(): JSX.Element {
  const [weekOf, setWeekOf] = useState('');
  const { data, loading, error, reload } = useQuery<Data>(`/instructors${weekOf ? `?date=${weekOf}` : ''}`);
  const [editing, setEditing] = useState<InstructorRow | null>(null);
  const [query, setQuery] = useState('');

  const instructors = useMemo(
    () => (data?.instructors ?? []).filter((i) => matchesSearch(
      query, i.name, i.ratings, i.nightQualified ? 'night' : '', i.isMe ? 'me you' : '',
    )),
    [data, query],
  );

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 10, justifyContent: 'space-between', gap: 12 }}>
        <div className="segmented">
          <button onClick={() => setWeekOf(shift(data.weekStart, -7))}>‹ Previous</button>
          <button onClick={() => setWeekOf('')}>This week</button>
          <button onClick={() => setWeekOf(shift(data.weekStart, 7))}>Next ›</button>
        </div>
        <SearchField
          value={query}
          onChange={setQuery}
          label="Search instructors"
          placeholder="Name or rating"
        />
        <span className="small muted">
          Week of {new Date(`${data.weekStart}T12:00:00Z`).toLocaleDateString('en-US', { day: 'numeric', month: 'long', timeZone: 'UTC' })}
        </span>
      </div>

      <div className="card">
        <div className="table__scroll">
          <div style={{ display: 'flex', background: 'var(--surface-3)', borderBottom: '1px solid var(--border)', minWidth: 760 }}>
            <div className="grid__gutter grid__gutter--head cfi__gutter">Instructor</div>
            {data.days.map((d) => (
              <div key={d.date} style={{
                flex: 1, padding: '5px 8px', fontSize: 10.5, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--muted)', borderLeft: '1px solid var(--tick)',
              }}>
                {d.dow} {d.day}
              </div>
            ))}
          </div>
          {instructors.length === 0 && (
            <div className="empty">Nothing matches &ldquo;{query}&rdquo;.</div>
          )}
          {instructors.map((i) => (
            <div key={i.id} style={{ display: 'flex', borderBottom: '1px solid var(--border-3)', minWidth: 760 }}>
              <div className="cfi__gutter" style={{ padding: '7px 8px', borderRight: '1px solid var(--border)', display: 'flex', gap: 7 }}>
                <Avatar userId={i.userId} updatedAt={i.avatarUpdatedAt} initials={initialsOf(i.name)} size={30} />
                <span style={{ minWidth: 0 }}>
                  <button
                    className="instructor__name"
                    onClick={() => setEditing(i)}
                    title={`Open ${i.name}`}
                  >
                    {i.name}{i.isMe && ' (you)'}
                  </button>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--muted-2)' }}>{i.ratings}</span>
                  <span style={{ display: 'block', fontSize: 11, color: i.todayHours > data.maxInstructionHours ? 'var(--conflict-c)' : 'var(--muted-2)' }}>
                    {i.todayHours.toFixed(1)} / {data.maxInstructionHours.toFixed(1)} today · {i.weekHours.toFixed(1)} this week
                  </span>
                </span>
              </div>
              {i.cells.map((c) => (
                <div
                  key={c.date}
                  title={c.state === 'over policy'
                    ? `Above the school policy of ${data.maxInstructionHours.toFixed(1)} hrs instruction. Not a federal limit.`
                    : undefined}
                  style={{
                    flex: 1, padding: '7px 8px', borderLeft: '1px solid var(--border-3)',
                    background: c.state === 'over policy' ? 'var(--conflict-bg)' : c.state === 'off' ? 'var(--hover)' : 'var(--surface)',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 12.5 }}>{c.hours > 0 ? c.hours.toFixed(1) : '—'}</div>
                  <div style={{ fontSize: 10.5, color: c.state === 'over policy' ? 'var(--conflict-c)' : 'var(--muted-2)' }}>{c.state}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="note">
        Duty limits shown are school policy ({data.maxInstructionHours.toFixed(1)} hrs instruction /
        {' '}{data.maxDutyHours.toFixed(1)} hrs duty), not federal limits.
      </div>

      {editing && (
        <InstructorDetail
          instructor={editing}
          canManage={data.canManage}
          maxInstructionHours={data.maxInstructionHours}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}
    </div>
  );
}

/**
 * What the cards used to say, opened from the instructor's name: who they are,
 * how the week has loaded them, and the working hours slot search honours.
 */
function InstructorDetail({ instructor, canManage, maxInstructionHours, onClose, onSaved }: {
  instructor: InstructorRow;
  canManage: boolean;
  maxInstructionHours: number;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [windows, setWindows] = useState(() =>
    Array.from({ length: 7 }, (_, weekday) => {
      const found = instructor.availability.find((a) => a.weekday === weekday);
      return {
        weekday,
        on: !!found,
        start: minutesToClock(found?.start_min ?? 8 * 60),
        end: minutesToClock(found?.end_min ?? 18 * 60),
      };
    }),
  );

  const save = async () => {
    try {
      await api.put(`/instructors/${instructor.id}/availability`, {
        windows: windows.filter((w) => w.on).map((w) => ({
          weekday: w.weekday,
          startMin: clockToMinutes(w.start),
          endMin: clockToMinutes(w.end),
        })),
      });
      toast.ok('Availability saved', 'Slot search will honour it from now on.');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err instanceof Error ? err.message : undefined);
    }
  };

  const booked = instructor.cells.filter((c) => c.hours > 0).length;
  const overPolicy = instructor.cells.filter((c) => c.state === 'over policy').length;

  return (
    <Drawer
      title={instructor.name}
      subtitle={instructor.ratings}
      onClose={onClose}
      footer={canManage
        ? <button className="btn btn--primary" onClick={save}>Save availability</button>
        : undefined}
    >
      <Panes>
        <Pane title="Instructor" flush>
          <div className="row" style={{ gap: 14, padding: '14px 24px 12px', flexWrap: 'nowrap' }}>
            <AvatarPicker
              userId={instructor.userId}
              updatedAt={instructor.avatarUpdatedAt}
              initials={initialsOf(instructor.name)}
              label={instructor.name}
              size={56}
              canEdit={canManage}
              onChanged={onSaved}
            />
          </div>
          <div className="row" style={{ gap: 6, padding: '0 24px 10px' }}>
            {instructor.isMe && <Pill tone="blue">You</Pill>}
            {instructor.cfiExpiresOn && (
              <Pill tone={expiringSoon(instructor.cfiExpiresOn) ? 'amber' : 'gray'}>
                CFI to {instructor.cfiExpiresOn}
              </Pill>
            )}
          </div>
          <div className="kv"><span>Ratings</span><span>{instructor.ratings}</span></div>
          <div className="kv">
            <span>Night instruction</span>
            <span>{instructor.nightQualified ? 'Yes' : 'Not scheduled'}</span>
          </div>
          <div className="kv">
            <span>Today</span>
            <span style={{ color: instructor.todayHours > maxInstructionHours ? 'var(--sys-red)' : undefined }}>
              {instructor.todayHours.toFixed(1)} / {maxInstructionHours.toFixed(1)} hrs
            </span>
          </div>
          <div className="kv"><span>This week</span><span>{instructor.weekHours.toFixed(1)} hrs</span></div>
          <div className="kv"><span>Days booked</span><span>{booked} of 7</span></div>
          {overPolicy > 0 && (
            <div className="kv">
              <span>Over policy</span>
              <span style={{ color: 'var(--sys-red)' }}>
                {overPolicy} day{overPolicy === 1 ? '' : 's'} this week
              </span>
            </div>
          )}
          <div className="kv">
            <span>Working days</span>
            <span>
              {instructor.availability.length
                ? instructor.availability.map((a) => DOW[a.weekday].slice(0, 3)).join(' ')
                : 'None set'}
            </span>
          </div>
        </Pane>

        <Pane title="Availability">
          <p className="small muted" style={{ margin: '0 0 12px', textWrap: 'pretty' }}>
            Slot search only offers times inside these windows.
            {!canManage && ' Only management can change them.'}
          </p>
          <div className="stack" style={{ gap: 8 }}>
          {windows.map((w, i) => (
            <div className="avail" key={w.weekday}>
              <label className="row avail__day" style={{ gap: 7 }}>
                <input
                  type="checkbox"
                  checked={w.on}
                  disabled={!canManage}
                  onChange={(e) => setWindows((ws) => ws.map((x, j) => (i === j ? { ...x, on: e.target.checked } : x)))}
                />
                {DOW[w.weekday]}
              </label>
              <span className="avail__span">
                <input className="input avail__time" type="time" value={w.start}
                  disabled={!w.on || !canManage}
                  onChange={(e) => setWindows((ws) => ws.map((x, j) => (i === j ? { ...x, start: e.target.value } : x)))} />
                <span className="muted">to</span>
                <input className="input avail__time" type="time" value={w.end}
                  disabled={!w.on || !canManage}
                  onChange={(e) => setWindows((ws) => ws.map((x, j) => (i === j ? { ...x, end: e.target.value } : x)))} />
              </span>
            </div>
          ))}
          </div>
        </Pane>
      </Panes>
    </Drawer>
  );
}

/** Two initials from a name, for the disc shown before a picture is added. */
function initialsOf(name: string): string {
  return name.replace(/\(.*\)/, '').trim().split(/\s+/).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

function minutesToClock(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}
function clockToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}
function shift(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function expiringSoon(date: string): boolean {
  return Date.parse(`${date}T12:00:00Z`) - Date.now() < 120 * 86400000;
}
